import React, { useState } from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { IDevice, PaymentFilter, PermissionNames } from '@getgreenline/homi-shared'
import { AppView, AppMainView } from '../../../../../components/AppView'
import AppHeader from '../../../../../components/AppView/Header'
import { ShiftSummaryModal } from '../../../../POS/ShiftSummaryModal'
import { PaymentListModal } from '../../../PaymentListModal'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { DashboardCashCountModal } from '../DashboardCashCountModal'
import Helmet from 'react-helmet'
import { CurrentUserStore } from '../../../../../stores/CurrentUserStore'
import { ShiftApi, ShiftModels } from '@getgreenline/shifts'
import { IBaseCursorPaginationFilter, ICursorPagination } from '@getgreenline/shared'
import { ShiftFilter } from './ShiftsFilter'
import {
  CustomTablePaginationV2,
  TPaginationPosition,
} from '../../../../../components/CustomTablePaginationV2'
import { ShiftsTable } from './ShiftsTable'
import { isUndefined } from 'lodash'
import { message } from 'antd-v4'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

const DEFAULT_LIMIT = 20

export const ShiftsV2: React.FC<Props> = inject(
  'currentCompanyStore',
  'currentUserStore',
)(
  observer(({ currentUserStore, currentCompanyStore }) => {
    const [selectedDeviceForModal, setSelectedDeviceForModal] = useState<IDevice | undefined>()
    const [selectedLocationId, setSelectedLocationId] = useState<number | undefined>()
    const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([])
    const [paginatedShifts, setPaginatedShifts] = useState<ShiftModels.IBaseShift[]>([])
    const [selectedShift, setSelectedShift] = useState<ShiftModels.IBaseShift | undefined>()
    const [selectedShiftForSales, setSelectedShiftForSales] =
      useState<ShiftModels.IBaseShift | undefined>()
    const [limit, setLimit] = useState<number>(DEFAULT_LIMIT)
    const [isShiftSummaryVisible, setIsShiftSummaryVisible] = useState<boolean>(false)
    const [isCashCounterModalVisible, setIsCashCounterModalVisible] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [pagination, setPagination] = useState<ICursorPagination | Record<string, never>>({})
    const [isFirstPage, setIsFirstPage] = useState<boolean>(false)

    const company = currentCompanyStore?.company
    if (!currentCompanyStore || !company) {
      return null
    }

    const userPermittedLocations = () => {
      const locations = currentCompanyStore?.locations
      return locations?.filter((location) =>
        currentUserStore!.hasPermissionAtEntity(PermissionNames.CAN_VIEW_REPORTS, location.id),
      )
    }

    if (!currentCompanyStore.devices) {
      currentCompanyStore.getDevices()
    }
    const companyDevices = currentCompanyStore.devices || []
    const locations = userPermittedLocations() || []

    const noLocationMessage = () => (
      <div className='d-flex p-4 ' style={{ alignItems: 'center', justifyContent: 'center' }}>
        <h3 style={{ color: '#C4C4C4', fontSize: '18px' }}>Please Select a Location </h3>
      </div>
    )

    const renderTable = () => {
      if (isUndefined(selectedLocationId) && paginatedShifts.length === 0) {
        return noLocationMessage()
      }

      return (
        <ShiftsTable
          shifts={paginatedShifts || []}
          devices={companyDevices}
          onDetailsClick={(shift) => {
            setSelectedShift(shift)
            setIsShiftSummaryVisible(true)
          }}
          onSalesClick={(shift) => {
            setSelectedShiftForSales(shift)
          }}
          onFloatCountClick={(shift, device) => {
            setSelectedShift(shift)
            setSelectedDeviceForModal(device)
            setIsCashCounterModalVisible(true)
          }}
          isLoading={isLoading}
        />
      )
    }

    const paymentFilter = new PaymentFilter(company.id, 30, 0)
    if (selectedShiftForSales) {
      paymentFilter.setShiftId(selectedShiftForSales.id)
    }

    const getShifts = async ({
      locationId,
      deviceIds,
      cursor = null,
    }: {
      locationId?: number
      deviceIds?: string[]
      cursor?: string | null
    }) => {
      if (isUndefined(locationId) && !cursor) {
        message.error('Please select a location')
        return
      }

      try {
        setPaginatedShifts([])
        setIsLoading(true)

        const payload: ShiftModels.IShiftFilter & IBaseCursorPaginationFilter = {
          locationIds: locationId ? [locationId] : [],
          limit,
          deviceIds,
        }

        if (cursor) {
          payload.cursor = cursor
        }

        const shiftsResponse = await ShiftApi.getCursorPaginatedShifts(company.id, payload)

        setPaginatedShifts(shiftsResponse.data)
        setPagination(shiftsResponse.pagination)
      } catch (error) {
        message.error(parseErrorMsg(error) || 'An error occurred while loading shifts')
      } finally {
        setIsLoading(false)
      }
    }

    const getShiftsFromCursor = async (cursor: string | null) => {
      await getShifts({ locationId: selectedLocationId, deviceIds: selectedDeviceIds, cursor })
    }

    return (
      <AppView column>
        <Helmet>
          <title>Reports | POS Shifts</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader title='POS Shifts'></AppHeader>
        <AppMainView className='intercom-pb-standard'>
          <ShiftFilter
            devices={companyDevices}
            locations={locations}
            applyFilter={(locationId: number, deviceIds: string[]) => {
              setIsFirstPage(true)
              setSelectedLocationId(locationId)
              setSelectedDeviceIds(deviceIds)
              getShifts({ locationId, deviceIds })
            }}
          />
          <CustomTablePaginationV2
            changePage={getShiftsFromCursor}
            data={paginatedShifts}
            paginationPosition={TPaginationPosition.TOP_RIGHT}
            pagination={pagination}
            limit={limit}
            paginationReset={isFirstPage}
            setPaginationFirstPage={setIsFirstPage}
            hideRowOptions
          >
            {renderTable()}
          </CustomTablePaginationV2>
        </AppMainView>
        {selectedShift && isShiftSummaryVisible && (
          <ShiftSummaryModal
            shiftId={selectedShift.id}
            onHide={() => setIsShiftSummaryVisible(false)}
          />
        )}
        {selectedShiftForSales && (
          <PaymentListModal
            filter={paymentFilter}
            onClose={() => setSelectedShiftForSales(undefined)}
          />
        )}

        {selectedShift && selectedDeviceForModal && (
          <DashboardCashCountModal
            selectedShift={selectedShift}
            selectedDevice={selectedDeviceForModal}
            visible={isCashCounterModalVisible}
            setIsCashCounterModalVisible={(isCashCounterModalVisible) =>
              setIsCashCounterModalVisible(isCashCounterModalVisible)
            }
          />
        )}
      </AppView>
    )
  }),
)
